import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import {
  BuildingOffice2Icon, MagnifyingGlassIcon, Squares2X2Icon,
  BellIcon, CpuChipIcon, FireIcon, SparklesIcon,
} from '@heroicons/react/24/outline';

// Heavy (full countries/cities dataset) — lazy-loaded so it stays out of the main bundle.
const TargetLocationsField = React.lazy(() => import('../components/settings/TargetLocationsField'));
import HypeSettings from '../components/settings/HypeSettings';
import { ChannelIcon } from '../components/settings/channelIcons';
import RedditSourcesCard from '../components/settings/RedditSourcesCard';
import YouTubeSourcesCard from '../components/settings/YouTubeSourcesCard';
import ComingSoonCatalog from '../components/settings/ComingSoonCatalog';
import ChannelList from '../components/settings/ChannelList';
import Drawer from '../components/settings/Drawer';
import { getLeadKeywords, updateLeadKeywords } from '../services/settings';
import { getProfile, updateProfile, analyzeBusiness, getSetupStatus, Suggestion, TargetLocation } from '../services/onboarding';

interface DataSource {
  platform: string;
  enabled: boolean;
  apiKey?: string;
  webhook?: string;
  keywords?: string[];
}

interface AgentStatus {
  agent_name: string;
  status: string;
  pid?: number;
  uptime_seconds?: number;
  total_processed: number;
}

interface FacebookGroup {
  id: string;
  group_name: string;
  group_url: string;
  enabled: boolean;
  keywords?: string[];
  scan_frequency_minutes: number;
  description?: string;
  member_count?: number;
  created_at: string;
  updated_at: string;
}

interface TelegramChannel {
  id: string;
  channel_name: string;
  channel_username: string;
  channel_url?: string;
  enabled: boolean;
  keywords?: string[];
  scan_frequency_minutes: number;
  description?: string;
  member_count?: number;
  is_public: boolean;
  created_at: string;
  updated_at: string;
}

const SettingsPage: React.FC = () => {
  const [saved, setSaved] = useState(false);
  const [agentStatuses, setAgentStatuses] = useState<Record<string, AgentStatus>>({});
  const [loading, setLoading] = useState(false);

  // Single company-wide lead keyword list (persisted to backend, drives all scrapers)
  const [companyKeywords, setCompanyKeywords] = useState<string>('');
  const [kwSaving, setKwSaving] = useState(false);
  const [kwSaved, setKwSaved] = useState(false);

  // Business profile + shared AI suggestion (per-section "Suggest with AI")
  const [website, setWebsite] = useState('');
  const [description, setDescription] = useState('');
  const [targetLocations, setTargetLocations] = useState<TargetLocation[]>([]);
  const [profileSaving, setProfileSaving] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState<Suggestion | null>(null);

  // Channels tab: box grid + which channel's config is open
  const [channelTab, setChannelTab] = useState<string | null>(null);
  const [connStatus, setConnStatus] = useState<{ facebook?: boolean; telegram?: boolean; reddit?: boolean; youtube?: boolean }>({});

  // Facebook connection status (per-company)
  const [fbStatus, setFbStatus] = useState<{
    connected: boolean; status: string; fb_user_id?: string | null;
    imported_at?: string | null; last_ok_at?: string | null; last_error?: string | null;
  }>({ connected: false, status: 'not_connected' });

  // Facebook groups state
  const [facebookGroups, setFacebookGroups] = useState<FacebookGroup[]>([]);
  const [showAddGroupModal, setShowAddGroupModal] = useState(false);
  const [newGroup, setNewGroup] = useState({
    group_name: '',
    group_url: '',
    enabled: true,
    keywords: [] as string[],
    scan_frequency_minutes: 60,
    description: ''
  });

  // Telegram channels state
  const [telegramChannels, setTelegramChannels] = useState<TelegramChannel[]>([]);
  const [showAddChannelModal, setShowAddChannelModal] = useState(false);
  const [newChannel, setNewChannel] = useState({
    channel_name: '',
    channel_username: '',
    channel_url: '',
    enabled: true,
    keywords: [] as string[],
    scan_frequency_minutes: 60,
    description: '',
    is_public: true
  });

  useEffect(() => {
    fetchAgentStatuses();
    fetchFacebookGroups();
    fetchTelegramChannels();
    fetchFbStatus();
    fetchKeywords();
    fetchProfile();
    refreshConnStatus();
    const interval = setInterval(fetchAgentStatuses, 5000);
    return () => clearInterval(interval);
  }, []);

  const refreshConnStatus = async () => {
    try { const s = await getSetupStatus(); setConnStatus(s.connections); } catch { /* ignore */ }
  };

  const fetchKeywords = async () => {
    try {
      const kw = await getLeadKeywords();
      setCompanyKeywords(kw.join(', '));
    } catch (error) {
      console.error('Failed to fetch keywords:', error);
    }
  };

  const fetchProfile = async () => {
    try {
      const p = await getProfile();
      setWebsite(p.website || '');
      setDescription(p.description || '');
      setTargetLocations(p.target_locations || []);
      if (p.last_suggestion) setAiSuggestion(p.last_suggestion);
    } catch (e) { console.error('Failed to fetch profile:', e); }
  };

  const saveProfile = async () => {
    setProfileSaving(true);
    try { await updateProfile(website.trim(), description.trim(), targetLocations); toast.success('Business profile saved'); }
    catch { toast.error('Could not save profile'); }
    finally { setProfileSaving(false); }
  };

  // Shared "Suggest with AI" — runs one analysis, caches it for all sections.
  const runSuggest = async (): Promise<Suggestion | null> => {
    if (!description.trim() && !targetLocations.length) {
      toast.error('Add a short description and your target locations in Business first.');
      return null;
    }
    setAiBusy(true);
    try {
      const s = await analyzeBusiness(website.trim(), description.trim(), targetLocations);
      setAiSuggestion(s);
      if (s._fallback) toast('AI unavailable — showing sensible defaults.', { icon: 'ℹ️' });
      return s;
    } catch { toast.error('AI suggestion failed. Try again.'); return null; }
    finally { setAiBusy(false); }
  };

  const suggestKeywords = async () => {
    const s = await runSuggest();
    if (s?.keywords?.length) { setCompanyKeywords(s.keywords.join(', ')); toast.success('Keywords suggested — review & save'); }
  };

  const handleSaveKeywords = async () => {
    setKwSaving(true);
    try {
      const list = companyKeywords.split(',').map(k => k.trim()).filter(Boolean);
      const saved = await updateLeadKeywords(list);
      setCompanyKeywords(saved.join(', '));  // reflect server-side dedupe/trim
      setKwSaved(true);
      setTimeout(() => setKwSaved(false), 3000);
    } catch (error) {
      console.error('Error saving keywords:', error);
      alert('❌ Failed to save keywords');
    } finally {
      setKwSaving(false);
    }
  };

  const fetchAgentStatuses = async () => {
    try {
      const response = await fetch('/api/v1/agents/all-status');
      const data = await response.json();
      const statusMap = data.agents.reduce((acc: Record<string, AgentStatus>, agent: AgentStatus) => {
        acc[agent.agent_name] = agent;
        return acc;
      }, {});
      setAgentStatuses(statusMap);
    } catch (error) {
      console.error('Failed to fetch agent statuses:', error);
    }
  };

  const startAgent = async (agentName: string) => {
    setLoading(true);
    try {
      const response = await fetch(`/api/v1/agents/start/${agentName}`, {
        method: 'POST',
      });
      const data = await response.json();
      if (data.success) {
        await fetchAgentStatuses();
      } else {
        alert(`Failed to start agent: ${data.message}`);
      }
    } catch (error) {
      console.error('Error starting agent:', error);
      alert('Failed to start agent');
    } finally {
      setLoading(false);
    }
  };

  const stopAgent = async (agentName: string) => {
    setLoading(true);
    try {
      const response = await fetch(`/api/v1/agents/stop/${agentName}`, {
        method: 'POST',
      });
      const data = await response.json();
      if (data.success) {
        await fetchAgentStatuses();
      } else {
        alert(`Failed to stop agent: ${data.message}`);
      }
    } catch (error) {
      console.error('Error stopping agent:', error);
      alert('Failed to stop agent');
    } finally {
      setLoading(false);
    }
  };

  const formatUptime = (seconds?: number): string => {
    if (!seconds) return '-';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hours > 0) return `${hours}h ${minutes}m ${secs}s`;
    if (minutes > 0) return `${minutes}m ${secs}s`;
    return `${secs}s`;
  };


  // Cookie import state
  const [cookieJson, setCookieJson] = useState('');
  const [cookieImporting, setCookieImporting] = useState(false);
  const [cookieResult, setCookieResult] = useState<string | null>(null);

  const importCookies = async () => {
    if (!cookieJson.trim()) return;
    setCookieImporting(true);
    setCookieResult(null);
    try {
      let parsed;
      try { parsed = JSON.parse(cookieJson); } catch { alert('Invalid JSON — paste the full array from Cookie-Editor'); return; }
      const cookies = Array.isArray(parsed) ? parsed : parsed.cookies ?? [];
      const res = await fetch('/api/v1/facebook/import-cookies', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cookies })
      });
      const data = await res.json();
      if (res.ok) {
        setCookieResult(`✅ Facebook connected${data.fb_user_id ? ` (id ${data.fb_user_id})` : ''}`);
        setCookieJson('');
        fetchFbStatus();
      } else {
        setCookieResult(`❌ ${data.detail || 'Import failed'}`);
      }
    } catch (e) {
      setCookieResult('❌ Network error');
    } finally {
      setCookieImporting(false);
    }
  };

  // Facebook connection status (per-company)
  const fetchFbStatus = async () => {
    try {
      const res = await fetch('/api/v1/facebook/session-status');
      if (res.ok) setFbStatus(await res.json());
    } catch {}
  };

  const disconnectFacebook = async () => {
    if (!confirm('Disconnect Facebook? Scraping will stop until reconnected.')) return;
    try {
      await fetch('/api/v1/facebook/disconnect', { method: 'POST' });
      fetchFbStatus();
    } catch {}
  };

  // Facebook groups functions
  const fetchFacebookGroups = async () => {
    try {
      const response = await fetch('/api/v1/facebook-groups');
      const data = await response.json();
      setFacebookGroups(data.groups || []);
    } catch (error) {
      console.error('Failed to fetch Facebook groups:', error);
    }
  };

  const addFacebookGroup = async () => {
    // Validation
    if (!newGroup.group_name || !newGroup.group_name.trim()) {
      alert('❌ Please provide a group name');
      return;
    }

    if (!newGroup.group_url || !newGroup.group_url.trim()) {
      alert('❌ Please provide a group URL');
      return;
    }

    // Validate Facebook URL format
    const url = newGroup.group_url.toLowerCase();
    if (!url.includes('facebook.com/groups/') && !url.includes('fb.com/groups/')) {
      alert('❌ Invalid URL. Please use a Facebook group URL like:\nhttps://facebook.com/groups/groupname');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch('/api/v1/facebook-groups', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...newGroup,
          group_name: newGroup.group_name.trim(),
          group_url: newGroup.group_url.trim()
        })
      });

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Group added successfully:', data);

        // Refresh the list
        await fetchFacebookGroups();

        // Close modal and reset form
        setShowAddGroupModal(false);
        setNewGroup({
          group_name: '',
          group_url: '',
          enabled: true,
          keywords: [],
          scan_frequency_minutes: 60,
          description: ''
        });

        alert('✅ Facebook group added successfully!');
      } else {
        const error = await response.json();
        let errorMessage = 'Failed to add group';

        if (error.detail) {
          if (typeof error.detail === 'string') {
            errorMessage = error.detail;
          } else if (Array.isArray(error.detail)) {
            errorMessage = error.detail.map((e: any) => e.msg).join(', ');
          }
        }

        alert(`❌ ${errorMessage}`);
      }
    } catch (error) {
      console.error('Error adding Facebook group:', error);
      alert('❌ Failed to connect to server. Please ensure:\n1. API server is running on port 8000\n2. You have restarted the server after adding Facebook groups feature');
    } finally {
      setLoading(false);
    }
  };

  const toggleFacebookGroup = async (groupId: string, enabled: boolean) => {
    try {
      const endpoint = enabled ? 'enable' : 'disable';
      const response = await fetch(`/api/v1/facebook-groups/${groupId}/${endpoint}`, {
        method: 'POST'
      });

      if (response.ok) {
        await fetchFacebookGroups();
      }
    } catch (error) {
      console.error('Error toggling Facebook group:', error);
    }
  };

  const deleteFacebookGroup = async (groupId: string, groupName: string) => {
    if (!confirm(`Are you sure you want to delete "${groupName}"?\n\nThis action cannot be undone.`)) {
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`/api/v1/facebook-groups/${groupId}`, {
        method: 'DELETE'
      });

      if (response.ok || response.status === 204) {
        console.log('✅ Group deleted successfully');
        await fetchFacebookGroups();
        alert('✅ Group deleted successfully');
      } else {
        const error = await response.json();
        alert(`❌ Failed to delete group: ${error.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error deleting Facebook group:', error);
      alert('❌ Failed to delete group. Please check your connection.');
    } finally {
      setLoading(false);
    }
  };

  // Telegram channels functions
  const fetchTelegramChannels = async () => {
    try {
      const response = await fetch('/api/v1/telegram-channels');
      const data = await response.json();
      setTelegramChannels(data.channels || []);
    } catch (error) {
      console.error('Failed to fetch Telegram channels:', error);
    }
  };

  const addTelegramChannel = async () => {
    // Validation
    if (!newChannel.channel_name || !newChannel.channel_name.trim()) {
      alert('❌ Please provide a channel name');
      return;
    }

    if (!newChannel.channel_username || !newChannel.channel_username.trim()) {
      alert('❌ Please provide a channel username');
      return;
    }

    // Validate channel username format
    let username = newChannel.channel_username.trim();
    if (username.startsWith('@')) {
      username = username.substring(1);
    }

    if (username.length < 3) {
      alert('❌ Channel username must be at least 3 characters');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch('/api/v1/telegram-channels', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...newChannel,
          channel_name: newChannel.channel_name.trim(),
          channel_username: username
        })
      });

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Channel added successfully:', data);

        // Refresh the list
        await fetchTelegramChannels();

        // Close modal and reset form
        setShowAddChannelModal(false);
        setNewChannel({
          channel_name: '',
          channel_username: '',
          channel_url: '',
          enabled: true,
          keywords: [],
          scan_frequency_minutes: 60,
          description: '',
          is_public: true
        });

        alert('✅ Telegram channel added successfully!');
      } else {
        const error = await response.json();
        let errorMessage = 'Failed to add channel';

        if (error.detail) {
          if (typeof error.detail === 'string') {
            errorMessage = error.detail;
          } else if (Array.isArray(error.detail)) {
            errorMessage = error.detail.map((e: any) => e.msg).join(', ');
          }
        }

        alert(`❌ ${errorMessage}`);
      }
    } catch (error) {
      console.error('Error adding Telegram channel:', error);
      alert('❌ Failed to connect to server. Please ensure the API server is running.');
    } finally {
      setLoading(false);
    }
  };

  const toggleTelegramChannel = async (channelId: string, enabled: boolean) => {
    try {
      const endpoint = enabled ? 'enable' : 'disable';
      const response = await fetch(`/api/v1/telegram-channels/${channelId}/${endpoint}`, {
        method: 'POST'
      });

      if (response.ok) {
        await fetchTelegramChannels();
      }
    } catch (error) {
      console.error('Error toggling Telegram channel:', error);
    }
  };

  const deleteTelegramChannel = async (channelId: string, channelName: string) => {
    if (!confirm(`Are you sure you want to delete "${channelName}"?\n\nThis action cannot be undone.`)) {
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`/api/v1/telegram-channels/${channelId}`, {
        method: 'DELETE'
      });

      if (response.ok || response.status === 204) {
        console.log('✅ Channel deleted successfully');
        await fetchTelegramChannels();
        alert('✅ Channel deleted successfully');
      } else {
        const error = await response.json();
        alert(`❌ Failed to delete channel: ${error.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error deleting Telegram channel:', error);
      alert('❌ Failed to delete channel. Please check your connection.');
    } finally {
      setLoading(false);
    }
  };

  const [section, setSection] = React.useState<'business' | 'keywords' | 'channels' | 'notifications' | 'agent' | 'hype'>('business');

  const navItems = [
    { id: 'business',      label: 'Business',       icon: BuildingOffice2Icon },
    { id: 'keywords',      label: 'Keywords',       icon: MagnifyingGlassIcon },
    { id: 'channels',      label: 'Channels',       icon: Squares2X2Icon },
    { id: 'notifications', label: 'Notifications',  icon: BellIcon },
    { id: 'agent',         label: 'Agent',          icon: CpuChipIcon },
    { id: 'hype',          label: 'Hype',           icon: FireIcon },
  ] as const;

  const sectionMeta: Record<string, { title: string; subtitle: string }> = {
    business:      { title: 'Business profile', subtitle: 'Tell us about your business so the AI can target the right leads.' },
    keywords:      { title: 'Lead keywords',    subtitle: 'The terms a prospect would use. Posts matching these get classified.' },
    channels:      { title: 'Channels',         subtitle: 'Where we look for leads. Connect a channel to start monitoring it.' },
    notifications: { title: 'Notifications',    subtitle: 'Where qualified leads are delivered.' },
    agent:         { title: 'Agent',            subtitle: 'Start, stop, and monitor your lead-detection agent.' },
    hype:          { title: 'Hype',             subtitle: 'Trend intelligence configuration.' },
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        <p className="text-sm text-gray-500 mt-0.5">Configure your business, keywords, channels, and agent</p>
      </div>

      <div className="flex flex-col md:flex-row gap-8 items-start">
        {/* Left section nav */}
        <nav className="w-full md:w-52 flex-shrink-0 flex md:flex-col gap-1 overflow-x-auto pb-1">
          {navItems.map(item => {
            const Icon = item.icon;
            const active = section === item.id;
            return (
              <button key={item.id} onClick={() => setSection(item.id)}
                className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium text-left whitespace-nowrap transition-colors ${
                  active ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50'
                }`}>
                <Icon className={`h-5 w-5 flex-shrink-0 ${active ? 'text-blue-600' : 'text-gray-400'}`} />
                {item.label}
              </button>
            );
          })}
        </nav>

        {/* Right content */}
        <div className="flex-1 min-w-0 w-full">
          <div className="mb-5">
            <h2 className="text-lg font-bold text-gray-900">{sectionMeta[section].title}</h2>
            <p className="text-sm text-gray-500 mt-0.5">{sectionMeta[section].subtitle}</p>
          </div>

      {/* ─── CHANNELS ───────────────────────────────────────────────────────── */}
      {section === 'channels' && (
        <div className="space-y-5">
          <ChannelList connections={connStatus} onOpen={setChannelTab} />
          <ComingSoonCatalog />
        </div>
      )}

      {/* Channel config slide-over drawer */}
      <Drawer
        open={!!channelTab}
        onClose={() => { setChannelTab(null); refreshConnStatus(); }}
        title={channelTab ? (
          <><ChannelIcon id={channelTab} className="w-5 h-5" />
          {channelTab === 'reddit' ? 'Reddit' : channelTab === 'youtube' ? 'YouTube' : channelTab === 'facebook' ? 'Facebook' : channelTab === 'telegram' ? 'Telegram' : ''}</>
        ) : ''}
      >
        {channelTab === 'reddit' && (
          <RedditSourcesCard
            suggested={aiSuggestion?.sources?.reddit || []}
            onSuggest={runSuggest}
            suggesting={aiBusy}
          />
        )}

        {channelTab === 'youtube' && <YouTubeSourcesCard />}

        {channelTab === 'telegram' && (
          <div className="space-y-5">
          <TelegramAuthCard />
          {/* Telegram Channels Management */}
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-50">
              <div>
                <h2 className="text-base font-semibold text-gray-900">Telegram Channels</h2>
                <p className="text-xs text-gray-500 mt-0.5">{telegramChannels.length} configured · click row to toggle</p>
              </div>
              <button onClick={() => setShowAddChannelModal(true)}
                className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700">
                + Add Channel
              </button>
            </div>
            {telegramChannels.length === 0 ? (
              <div className="text-center py-12 text-gray-400 text-sm">No channels yet — click Add Channel</div>
            ) : (
              <div className="divide-y divide-gray-50 max-h-96 overflow-y-auto">
                {telegramChannels.map(ch => (
                  <div key={ch.id} className="flex items-center gap-3 px-5 py-3 hover:bg-gray-50/60 transition-colors">
                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${ch.enabled ? 'bg-emerald-500' : 'bg-gray-300'}`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate">{ch.channel_name}</p>
                      <p className="text-xs text-gray-400">@{ch.channel_username}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button onClick={() => toggleTelegramChannel(ch.id, ch.enabled)}
                        className={`px-2.5 py-1 rounded-full text-xs font-medium ${ch.enabled ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}>
                        {ch.enabled ? 'Active' : 'Disabled'}
                      </button>
                      <button onClick={() => deleteTelegramChannel(ch.id, ch.channel_name)}
                        className="p-1 text-gray-300 hover:text-red-500 transition-colors">
                        <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4"><path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd"/></svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          </div>
          )}

      {/* ─── FACEBOOK (in drawer) ───────────────────────────────────────────── */}
      {channelTab === 'facebook' && (
        <div className="space-y-5">
          {/* Connection status */}
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className={`w-2.5 h-2.5 rounded-full ${fbStatus.connected ? 'bg-emerald-500' : fbStatus.status === 'expired' ? 'bg-amber-500' : 'bg-gray-300'}`} />
              <div>
                <p className="text-sm font-semibold text-gray-900">
                  {fbStatus.connected ? 'Facebook Connected' : fbStatus.status === 'expired' ? 'Session Expired — reconnect' : 'Facebook Not Connected'}
                </p>
                <p className="text-xs text-gray-400">
                  {fbStatus.fb_user_id ? `FB id ${fbStatus.fb_user_id}` : 'Paste cookies below to connect'}
                  {fbStatus.last_ok_at ? ` · last scrape ${new Date(fbStatus.last_ok_at).toLocaleString()}` : ''}
                </p>
                {fbStatus.last_error && <p className="text-xs text-red-500 mt-0.5">{fbStatus.last_error}</p>}
              </div>
            </div>
            {fbStatus.connected && (
              <button onClick={disconnectFacebook} className="px-3 py-1.5 bg-gray-100 text-gray-600 rounded-lg text-sm hover:bg-gray-200">Disconnect</button>
            )}
          </div>

          {/* Cookie import */}
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
            <div className="mb-4">
              <h2 className="text-base font-semibold text-gray-900">{fbStatus.connected ? 'Reconnect Facebook' : 'Connect Facebook'} <span className="text-xs font-normal text-gray-400 ml-1">Free · session lasts weeks</span></h2>
              <p className="text-xs text-gray-500 mt-1">Install <strong>Cookie-Editor</strong> in Chrome → go to facebook.com → Export as JSON → paste below.</p>
            </div>
            <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 mb-3 text-xs text-blue-700">
              1. Install <strong>Cookie-Editor</strong> Chrome extension &nbsp;·&nbsp; 2. Open <strong>facebook.com</strong> (logged in) &nbsp;·&nbsp; 3. Click extension → Export → Export as JSON &nbsp;·&nbsp; 4. Paste below
            </div>
            <textarea value={cookieJson} onChange={e => setCookieJson(e.target.value)}
              placeholder='[{"name":"c_user","value":"...","domain":".facebook.com",...}]'
              className="w-full h-24 p-3 border border-gray-200 rounded-lg font-mono text-xs resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
            <div className="flex items-center gap-3 mt-3">
              <button onClick={importCookies} disabled={cookieImporting || !cookieJson.trim()}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
                {cookieImporting ? 'Importing…' : 'Import Cookies'}
              </button>
              {cookieResult && <span className="text-sm font-medium">{cookieResult}</span>}
            </div>
          </div>

          {/* Facebook Groups */}
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-50">
              <div>
                <h2 className="text-base font-semibold text-gray-900">Facebook Groups</h2>
                <p className="text-xs text-gray-500 mt-0.5">{facebookGroups.length} configured</p>
              </div>
              <button onClick={() => setShowAddGroupModal(true)}
                className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700">
                + Add Group
              </button>
            </div>
            {facebookGroups.length === 0 ? (
              <div className="text-center py-12 text-gray-400 text-sm">No groups yet — click Add Group</div>
            ) : (
              <div className="divide-y divide-gray-50 max-h-96 overflow-y-auto">
                {facebookGroups.map(g => (
                  <div key={g.id} className="flex items-center gap-3 px-5 py-3 hover:bg-gray-50/60 transition-colors">
                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${g.enabled ? 'bg-emerald-500' : 'bg-gray-300'}`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate">{g.group_name}</p>
                      <p className="text-xs text-gray-400 truncate">{g.group_url}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button onClick={() => toggleFacebookGroup(g.id, g.enabled)}
                        className={`px-2.5 py-1 rounded-full text-xs font-medium ${g.enabled ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}>
                        {g.enabled ? 'Active' : 'Disabled'}
                      </button>
                      <button onClick={() => deleteFacebookGroup(g.id, g.group_name)}
                        className="p-1 text-gray-300 hover:text-red-500 transition-colors">
                        <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4"><path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd"/></svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
      </Drawer>

      {/* ─── BUSINESS ───────────────────────────────────────────────────────── */}
      {section === 'business' && (
        <div className="space-y-5">
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">Website</label>
                <input value={website} onChange={e => setWebsite(e.target.value)}
                  placeholder="https://yourcompany.com"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">What does your business do?</label>
                <textarea value={description} onChange={e => setDescription(e.target.value)}
                  placeholder="e.g. We provide serviced apartments for monthly stays."
                  className="w-full h-24 p-3 border border-gray-200 rounded-lg bg-gray-50 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y" />
              </div>

              {/* Target locations */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Target locations</label>
                <p className="text-xs text-gray-400 mb-2">The cities &amp; countries you serve — used to target leads to the right markets.</p>
                <React.Suspense fallback={<div className="text-xs text-gray-400 py-2">Loading locations…</div>}>
                  <TargetLocationsField value={targetLocations} onChange={setTargetLocations} />
                </React.Suspense>
              </div>
            </div>
            <div className="mt-4 flex justify-end">
              <button onClick={saveProfile} disabled={profileSaving}
                className="px-5 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
                {profileSaving ? 'Saving…' : 'Save profile'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── KEYWORDS ───────────────────────────────────────────────────────── */}
      {section === 'keywords' && (
        <div className="space-y-5">
          {/* Keywords */}
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
            <div className="flex items-start justify-between mb-1">
              <h2 className="text-base font-semibold text-gray-900">Lead Keywords</h2>
              <button onClick={suggestKeywords} disabled={aiBusy}
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-gray-700 border border-gray-200 hover:bg-gray-50 px-3 py-1.5 rounded-lg disabled:opacity-50">
                <SparklesIcon className="w-4 h-4" /> {aiBusy ? 'Thinking…' : 'Suggest with AI'}
              </button>
            </div>
            <p className="text-xs text-gray-500 mb-4">
              Comma-separated keywords or phrases. A post is collected only if it contains one of them,
              then classified for lead quality. Leave empty to pause detection.
            </p>
            <textarea
              value={companyKeywords}
              onChange={e => setCompanyKeywords(e.target.value)}
              placeholder={"serviced apartment, monthly rental, looking for apartment, relocating to dubai"}
              className="w-full h-48 p-3 border border-gray-200 rounded-lg bg-gray-50 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y"
            />
            <div className="mt-1 text-xs text-gray-400">
              {companyKeywords.split(',').map(k => k.trim()).filter(Boolean).length} keywords
            </div>
            <div className="mt-4 flex justify-end">
              <button onClick={handleSaveKeywords} disabled={kwSaving}
                className="px-5 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
                {kwSaving ? 'Saving…' : kwSaved ? '✓ Saved' : 'Save Keywords'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── HYPE TAB ────────────────────────────────────────────────────────── */}
      {section === 'hype' && <HypeSettings />}

      {/* ─── NOTIFICATIONS TAB ───────────────────────────────────────────────── */}
      {section === 'notifications' && (
        <div>
          <div className="mb-4">
            <h2 className="text-base font-semibold text-gray-900">Notification Channels</h2>
            <p className="text-xs text-gray-500 mt-0.5">Where to send qualified leads. Add credentials to <code className="bg-gray-100 px-1 rounded">.env</code> then restart the agent.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <IntegrationCard IconComponent={MattermostIcon} name="Mattermost" status="active" statusLabel="Configured"
              accentColor="text-blue-700" accentBg="bg-blue-50"
              description="Already wired up. Qualified leads post to ai-leads-generation channel."
              envVars={["MATTERMOST_WEBHOOK_URL"]}
              setupSteps={["Mattermost: Main Menu → Integrations → Incoming Webhooks","Click 'Add Incoming Webhook', choose channel","  MATTERMOST_WEBHOOK_URL=https://your-mattermost.com/hooks/xxx"]} />
            <IntegrationCard IconComponent={SlackIcon} name="Slack" status="pending" statusLabel="Not configured"
              accentColor="text-green-600" accentBg="bg-green-50"
              description="Post qualified leads to a Slack channel via Incoming Webhooks."
              envVars={["SLACK_WEBHOOK_URL"]}
              setupSteps={["api.slack.com/apps → Create New App → Incoming Webhooks","Add New Webhook to Workspace → pick channel","  SLACK_WEBHOOK_URL=https://hooks.slack.com/services/xxx"]} />
            <IntegrationCard IconComponent={EmailIcon} name="Email (SMTP)" status="pending" statusLabel="Not configured"
              accentColor="text-gray-600" accentBg="bg-gray-100"
              description="Email leads to your team. Works with Gmail, Outlook, or any SMTP server."
              envVars={["SMTP_HOST","SMTP_PORT","SMTP_USER","SMTP_PASSWORD","SMTP_FROM","LEADS_EMAIL_TO"]}
              setupSteps={["Gmail: Enable 2FA → App Passwords → generate","  SMTP_HOST=smtp.gmail.com","  SMTP_PORT=587","  SMTP_USER=you@gmail.com","  SMTP_PASSWORD=your-app-password","  LEADS_EMAIL_TO=sales@estaie.com"]} />
            <IntegrationCard IconComponent={TelegramBotIcon} name="Telegram Bot" status="pending" statusLabel="Not configured"
              accentColor="text-sky-500" accentBg="bg-sky-50"
              description="Instant lead alerts to a Telegram group via a bot you own."
              envVars={["TELEGRAM_BOT_TOKEN","TELEGRAM_CHAT_ID"]}
              setupSteps={["Open Telegram → @BotFather → /newbot → copy token","Add bot to your group/channel","Get chat ID via api.telegram.org/bot<TOKEN>/getUpdates","  TELEGRAM_BOT_TOKEN=12345:ABCxxx","  TELEGRAM_CHAT_ID=-100xxxxxxxxxx"]} />
            <IntegrationCard IconComponent={WhatsAppIcon} name="WhatsApp (Twilio)" status="pending" statusLabel="Not configured"
              accentColor="text-green-600" accentBg="bg-green-50"
              description="Lead alerts via WhatsApp Business API. Sandbox free for testing."
              envVars={["TWILIO_ACCOUNT_SID","TWILIO_AUTH_TOKEN","TWILIO_WHATSAPP_FROM","WHATSAPP_TO"]}
              setupSteps={["Sign up at twilio.com → Messaging → Try WhatsApp","  TWILIO_ACCOUNT_SID=ACxxx","  TWILIO_AUTH_TOKEN=xxx","  TWILIO_WHATSAPP_FROM=whatsapp:+14155238886","  WHATSAPP_TO=whatsapp:+971xxxxxxxxx"]} />
            <IntegrationCard IconComponent={WebhookIcon} name="Custom Webhook" status="pending" statusLabel="Not configured"
              accentColor="text-violet-600" accentBg="bg-violet-50"
              description="POST leads as JSON to any endpoint — CRM, Zapier, n8n, or your own API."
              envVars={["LEADS_WEBHOOK_URL","LEADS_WEBHOOK_SECRET"]}
              setupSteps={["Set up a receiver (Zapier, n8n, custom endpoint)","  LEADS_WEBHOOK_URL=https://your-endpoint.com/leads","Optional: LEADS_WEBHOOK_SECRET=your-hmac-secret","Payload: {id, platform, content, budget, location, confidence}"]} />
          </div>
        </div>
      )}

      {/* ─── AGENT TAB ───────────────────────────────────────────────────────── */}
      {section === 'agent' && (
        <div className="space-y-4 max-w-2xl">
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-semibold text-gray-900">Agent Control</h2>
              <button onClick={fetchAgentStatuses}
                className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4"><path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd"/></svg>
              </button>
            </div>
            <div className="space-y-3">
              {/* Lead Detection */}
              <div className={`flex items-center justify-between p-4 rounded-xl border ${agentStatuses.lead_detection?.status === 'running' ? 'bg-emerald-50 border-emerald-200' : 'bg-gray-50 border-gray-200'}`}>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-semibold text-gray-900">Lead Detection Agent</span>
                    {agentStatuses.lead_detection?.status === 'running' && (
                      <span className="flex items-center gap-1.5 px-2 py-0.5 text-xs font-semibold rounded-full bg-emerald-100 text-emerald-700">
                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />Running
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500">Monitors Reddit, Telegram, Facebook for extended stay leads (30+ nights UAE)</p>
                  {agentStatuses.lead_detection?.status === 'running' && (
                    <p className="text-xs text-gray-400 mt-1">
                      Uptime: {formatUptime(agentStatuses.lead_detection.uptime_seconds)} &nbsp;·&nbsp; PID: {agentStatuses.lead_detection.pid}
                    </p>
                  )}
                </div>
                <div className="ml-4">
                  {agentStatuses.lead_detection?.status === 'running' ? (
                    <button onClick={() => stopAgent('lead_detection')} disabled={loading}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50">
                      Stop
                    </button>
                  ) : (
                    <button onClick={() => startAgent('lead_detection')} disabled={loading}
                      className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 disabled:opacity-50">
                      Start
                    </button>
                  )}
                </div>
              </div>

              {/* Sales Agent — coming soon */}
              <div className="flex items-center justify-between p-4 rounded-xl border border-gray-100 bg-gray-50/50 opacity-60">
                <div>
                  <span className="text-sm font-semibold text-gray-700">Sales Agent</span>
                  <p className="text-xs text-gray-400 mt-0.5">Auto-generates personalised outreach messages</p>
                </div>
                <span className="px-3 py-1 text-xs font-medium rounded-full bg-amber-100 text-amber-700">Coming Soon</span>
              </div>

              {/* Nurturing Agent — coming soon */}
              <div className="flex items-center justify-between p-4 rounded-xl border border-gray-100 bg-gray-50/50 opacity-60">
                <div>
                  <span className="text-sm font-semibold text-gray-700">Lead Nurturing Agent</span>
                  <p className="text-xs text-gray-400 mt-0.5">Manages follow-ups and engagement sequences</p>
                </div>
                <span className="px-3 py-1 text-xs font-medium rounded-full bg-amber-100 text-amber-700">Coming Soon</span>
              </div>
            </div>
          </div>
        </div>
      )}
        </div>
      </div>

      {/* ─── MODALS ───────────────────────────────────────────────────────────── */}
      {/* Add Channel Modal */}
      {showAddChannelModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Add Telegram Channel</h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Channel Name *
                </label>
                <input
                  type="text"
                  value={newChannel.channel_name}
                  onChange={(e) => setNewChannel({...newChannel, channel_name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900"
                  placeholder="e.g., Dubai Property Listings"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Channel Username *
                </label>
                <input
                  type="text"
                  value={newChannel.channel_username}
                  onChange={(e) => setNewChannel({...newChannel, channel_username: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900"
                  placeholder="@channelname or channelname"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Enter the channel username (with or without @)
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Channel URL (optional)
                </label>
                <input
                  type="text"
                  value={newChannel.channel_url}
                  onChange={(e) => setNewChannel({...newChannel, channel_url: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900"
                  placeholder="https://t.me/channelname"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description (optional)
                </label>
                <textarea
                  value={newChannel.description}
                  onChange={(e) => setNewChannel({...newChannel, description: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900"
                  rows={2}
                  placeholder="Brief description of this channel"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Scan Frequency (minutes)
                </label>
                <input
                  type="number"
                  value={newChannel.scan_frequency_minutes}
                  onChange={(e) => setNewChannel({...newChannel, scan_frequency_minutes: parseInt(e.target.value) || 60})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900"
                  min="5"
                  max="1440"
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={newChannel.is_public}
                  onChange={(e) => setNewChannel({...newChannel, is_public: e.target.checked})}
                  className="w-4 h-4 text-blue-600 rounded"
                />
                <label className="text-sm text-gray-700">
                  Public channel
                </label>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={newChannel.enabled}
                  onChange={(e) => setNewChannel({...newChannel, enabled: e.target.checked})}
                  className="w-4 h-4 text-blue-600 rounded"
                />
                <label className="text-sm text-gray-700">
                  Enable monitoring immediately
                </label>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={addTelegramChannel}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
              >
                Add Channel
              </button>
              <button
                onClick={() => {
                  setShowAddChannelModal(false);
                  setNewChannel({
                    channel_name: '',
                    channel_username: '',
                    channel_url: '',
                    enabled: true,
                    keywords: [],
                    scan_frequency_minutes: 60,
                    description: '',
                    is_public: true
                  });
                }}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Group Modal */}
      {showAddGroupModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Add Facebook Group</h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Group Name *
                </label>
                <input
                  type="text"
                  value={newGroup.group_name}
                  onChange={(e) => setNewGroup({...newGroup, group_name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900"
                  placeholder="e.g., Dubai Expats Community"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Group URL *
                </label>
                <input
                  type="text"
                  value={newGroup.group_url}
                  onChange={(e) => setNewGroup({...newGroup, group_url: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900"
                  placeholder="https://facebook.com/groups/groupname"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Enter the full Facebook group URL
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description (optional)
                </label>
                <textarea
                  value={newGroup.description}
                  onChange={(e) => setNewGroup({...newGroup, description: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900"
                  rows={2}
                  placeholder="Brief description of this group"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Scan Frequency (minutes)
                </label>
                <input
                  type="number"
                  value={newGroup.scan_frequency_minutes}
                  onChange={(e) => setNewGroup({...newGroup, scan_frequency_minutes: parseInt(e.target.value) || 60})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900"
                  min="5"
                  max="1440"
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={newGroup.enabled}
                  onChange={(e) => setNewGroup({...newGroup, enabled: e.target.checked})}
                  className="w-4 h-4 text-blue-600 rounded"
                />
                <label className="text-sm text-gray-700">
                  Enable monitoring immediately
                </label>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={addFacebookGroup}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
              >
                Add Group
              </button>
              <button
                onClick={() => {
                  setShowAddGroupModal(false);
                  setNewGroup({
                    group_name: '',
                    group_url: '',
                    enabled: true,
                    keywords: [],
                    scan_frequency_minutes: 60,
                    description: ''
                  });
                }}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

/* ── Integration Card Component ─────────────────────────────────────────── */

const MattermostIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
    <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm.195 4.25c3.678.047 7.04 2.67 7.04 7.064 0 4.896-3.87 7.75-7.235 7.75-1.933 0-3.656-.79-4.83-2.04L5.5 18.5l1.478-2.712c-.646-.917-1.023-2.04-1.023-3.224 0-3.94 3.092-8.314 6.24-8.314zm.042 1.953c-2.36 0-4.516 3.432-4.516 6.36 0 .866.218 1.663.604 2.33l-.898 1.648 1.802-.487c.892.692 2.024 1.108 3.25 1.108 2.543 0 5.48-2.196 5.48-6.035 0-3.355-2.52-4.924-5.722-4.924z"/>
  </svg>
);

const SlackIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
    <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zm1.271 0a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zm0 1.271a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zm10.123 2.521a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zm-1.268 0a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312zm-2.523 10.123a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zm0-1.268a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z"/>
  </svg>
);

const EmailIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75"/>
  </svg>
);

const TelegramBotIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
  </svg>
);

const WhatsAppIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z"/>
  </svg>
);

const WebhookIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244"/>
  </svg>
);

interface IntegrationCardProps {
  IconComponent: React.FC;
  name: string;
  status: 'active' | 'pending';
  statusLabel: string;
  description: string;
  envVars: string[];
  setupSteps: string[];
  accentColor: string;
  accentBg: string;
}

const IntegrationCard: React.FC<IntegrationCardProps> = ({
  IconComponent, name, status, statusLabel, description, envVars, setupSteps, accentColor, accentBg
}) => {
  const [expanded, setExpanded] = React.useState(false);
  return (
    <div className={`border rounded-xl overflow-hidden transition-all ${status === 'active' ? 'border-emerald-200' : 'border-gray-100'}`}>
      <div className="p-4 bg-white">
        <div className="flex items-start gap-3">
          <div className={`flex-shrink-0 w-10 h-10 rounded-xl ${accentBg} flex items-center justify-center`}>
            <span className={accentColor}><IconComponent /></span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-semibold text-gray-900 text-sm">{name}</h3>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                {statusLabel}
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{description}</p>
          </div>
          <button
            onClick={() => setExpanded(e => !e)}
            className="flex-shrink-0 text-xs text-blue-600 hover:text-blue-700 font-medium whitespace-nowrap ml-1"
          >
            {expanded ? 'Hide ↑' : 'Guide ↓'}
          </button>
        </div>
      </div>

      {expanded && (
        <div className="border-t border-gray-50 bg-gray-50/60 p-4 space-y-3">
          <div>
            <p className="text-xs font-semibold text-gray-600 mb-1.5">Required in <code className="bg-gray-200 px-1 rounded text-gray-700">.env</code></p>
            <div className="flex flex-wrap gap-1.5">
              {envVars.map(v => (
                <code key={v} className="px-2 py-1 bg-white border border-gray-200 text-gray-700 text-xs rounded-lg font-mono shadow-sm">{v}</code>
              ))}
            </div>
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-600 mb-2">Setup steps</p>
            <ol className="space-y-1.5">
              {setupSteps.map((step, i) => (
                <li key={i} className="flex gap-2.5">
                  {step.startsWith('  ') ? (
                    <code className="ml-6 bg-white border border-gray-200 px-2 py-1 rounded-lg font-mono text-xs text-gray-700 block w-full shadow-sm">{step.trim()}</code>
                  ) : (
                    <>
                      <span className="flex-shrink-0 w-4 h-4 rounded-full bg-blue-600 text-white font-bold text-[10px] flex items-center justify-center mt-0.5">{i + 1}</span>
                      <span className="text-xs text-gray-600">{step}</span>
                    </>
                  )}
                </li>
              ))}
            </ol>
          </div>
          <p className="text-xs text-gray-400 italic">After editing .env: <code className="bg-gray-200 px-1 rounded">sudo systemctl restart estaie-agent</code></p>
        </div>
      )}
    </div>
  );
};

export default SettingsPage;

/* ── Telegram API Auth Component ─────────────────────────────────────────── */
const TelegramAuthCard: React.FC = () => {
  const [status, setStatus] = React.useState<{authorized: boolean; phone?: string} | null>(null);
  const [step, setStep] = React.useState<'idle' | 'code_sent'>('idle');
  const [phone, setPhone] = React.useState('');
  const [code, setCode] = React.useState('');
  const [twoFA, setTwoFA] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [msg, setMsg] = React.useState('');

  React.useEffect(() => {
    fetch('/api/v1/telegram-auth/status').then(r => r.json()).then(d => {
      setStatus(d);
      if (d.phone) setPhone(d.phone);
    }).catch(() => {});
  }, []);

  const sendCode = async () => {
    if (!phone.trim()) { setMsg('Enter a phone number first'); return; }
    setLoading(true); setMsg('');
    try {
      const r = await fetch('/api/v1/telegram-auth/send-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: phone.trim() })
      });
      const d = await r.json();
      if (d.status === 'already_authorized') {
        setStatus({ authorized: true, phone }); setMsg('Already authorized.');
      } else {
        setStep('code_sent'); setMsg(d.message || `Code sent to ${phone}`);
      }
    } catch { setMsg('Request failed'); }
    setLoading(false);
  };

  const verifyCode = async () => {
    setLoading(true); setMsg('');
    try {
      const r = await fetch('/api/v1/telegram-auth/verify-code', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code, two_factor_password: twoFA })
      });
      const d = await r.json();
      if (d.status === 'authorized') {
        setStatus({ authorized: true, phone: d.phone }); setStep('idle');
        setMsg(`✅ ${d.message}`);
      } else if (d.status === '2fa_required') {
        setMsg('Enter your 2FA password and submit again.');
      } else {
        setMsg(d.detail || 'Verification failed');
      }
    } catch { setMsg('Request failed'); }
    setLoading(false);
  };

  const isOk = status?.authorized;

  return (
    <div className={`bg-white rounded-xl border p-5 mb-4 ${isOk ? 'border-emerald-200 bg-emerald-50/20' : 'border-amber-200'}`}>
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${isOk ? 'bg-emerald-100' : 'bg-sky-100'}`}>
          <svg viewBox="0 0 24 24" className={`w-5 h-5 ${isOk ? 'text-emerald-600' : 'text-sky-500'}`} fill="currentColor">
            <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
          </svg>
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-gray-900 text-sm">Telegram API Session</span>
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${isOk ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
              {isOk ? `Authorized · ${status?.phone}` : 'Not authorized'}
            </span>
          </div>
          <p className="text-xs text-gray-500 mt-0.5">
            {isOk ? 'Full group access active — scraping real community discussions where seekers post'
                   : 'Authorize your Telegram account to access groups (not just broadcast channels)'}
          </p>
        </div>
      </div>

      {!isOk && (
        <div className="space-y-3 mt-3 pt-3 border-t border-gray-100">
          <div className="flex gap-3 flex-wrap">
            <div className="flex-1 min-w-48">
              <label className="text-xs font-medium text-gray-600 mb-1 block">Phone number (with country code)</label>
              <input
                type="tel" value={phone} onChange={e => setPhone(e.target.value)}
                placeholder="+971582974424"
                disabled={step === 'code_sent'}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-sky-500 disabled:bg-gray-50"
              />
            </div>
            {step === 'idle' && (
              <div className="flex items-end">
                <button onClick={sendCode} disabled={loading || !phone.trim()}
                  className="px-4 py-2 bg-sky-600 text-white rounded-lg text-sm font-medium hover:bg-sky-700 disabled:opacity-50 whitespace-nowrap">
                  {loading ? 'Sending…' : 'Send Code'}
                </button>
              </div>
            )}
          </div>

          {step === 'code_sent' && (
            <div className="space-y-2">
              <div className="p-3 bg-sky-50 border border-sky-200 rounded-lg text-xs text-sky-800">{msg}</div>
              <div className="flex gap-2 flex-wrap">
                <input type="text" value={code} onChange={e => setCode(e.target.value)}
                  placeholder="Verification code from Telegram"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-sky-500" />
                <input type="password" value={twoFA} onChange={e => setTwoFA(e.target.value)}
                  placeholder="2FA password (if enabled)"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-sky-500" />
                <button onClick={verifyCode} disabled={loading || !code}
                  className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 disabled:opacity-50">
                  {loading ? 'Verifying…' : 'Authorize'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {msg && step === 'idle' && (
        <p className={`mt-2 text-xs font-medium ${msg.startsWith('✅') ? 'text-emerald-700' : 'text-red-600'}`}>{msg}</p>
      )}
    </div>
  );
};

/* ── Facebook Paid Options Component ────────────────────────────────────────── */
const FacebookPaidOptions: React.FC = () => {
  const [expanded, setExpanded] = React.useState(false);
  const options = [
    { name: 'Bright Data', price: 'From $1.50/GB', desc: 'Largest residential proxy network. 72M+ IPs, best Facebook success rate.', url: 'https://brightdata.com', env: 'FB_PROXY_HOST=brd.superproxy.io\nFB_PROXY_PORT=22225\nFB_PROXY_USER=your_username\nFB_PROXY_PASS=your_password' },
    { name: 'Smartproxy', price: 'From $7/month', desc: '40M+ residential IPs. Simple pricing, good UAE coverage.', url: 'https://smartproxy.com', env: 'FB_PROXY_HOST=gate.smartproxy.com\nFB_PROXY_PORT=7000\nFB_PROXY_USER=your_username\nFB_PROXY_PASS=your_password' },
    { name: 'IPRoyal', price: 'From $3/month', desc: 'Affordable rotating residential proxies. Pay-as-you-go.', url: 'https://iproyal.com', env: 'FB_PROXY_HOST=geo.iproyal.com\nFB_PROXY_PORT=12321\nFB_PROXY_USER=your_username\nFB_PROXY_PASS=your_password' },
    { name: 'Oxylabs', price: 'From $15/month', desc: 'Enterprise-grade. Dedicated UAE residential IPs available.', url: 'https://oxylabs.io', env: 'FB_PROXY_HOST=pr.oxylabs.io\nFB_PROXY_PORT=7777\nFB_PROXY_USER=your_username\nFB_PROXY_PASS=your_password' },
  ];
  return (
    <div className="border border-blue-100 rounded-xl bg-white">
      <button onClick={() => setExpanded(e => !e)} className="w-full flex items-center justify-between p-4 text-left">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
            <svg viewBox="0 0 24 24" className="w-4 h-4 text-blue-600" fill="currentColor">
              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
            </svg>
          </div>
          <div>
            <span className="text-sm font-semibold text-gray-900">Enable Facebook Group Scraping</span>
            <p className="text-xs text-gray-500">Requires a residential proxy (~$5–15/month). GCP IPs are blocked by Facebook.</p>
          </div>
        </div>
        <span className="text-xs text-blue-600 font-medium flex-shrink-0">{expanded ? 'Hide ↑' : 'View options ↓'}</span>
      </button>
      {expanded && (
        <div className="border-t border-gray-50 p-4 space-y-3">
          <p className="text-xs text-gray-500 bg-amber-50 border border-amber-100 rounded-lg p-3">
            <strong>Why paid:</strong> Facebook blocks all cloud/datacenter IPs. A residential proxy routes requests through real home connections. Once configured, add <code className="bg-gray-100 px-1 rounded">FB_PROXY_HOST</code>, <code className="bg-gray-100 px-1 rounded">FB_PROXY_PORT</code>, <code className="bg-gray-100 px-1 rounded">FB_PROXY_USER</code>, <code className="bg-gray-100 px-1 rounded">FB_PROXY_PASS</code> to your .env then restart the agent.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {options.map(o => (
              <div key={o.name} className="border border-gray-100 rounded-xl p-3 bg-gray-50/50">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-semibold text-gray-800">{o.name}</span>
                  <span className="text-xs font-medium text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">{o.price}</span>
                </div>
                <p className="text-xs text-gray-500 mb-2">{o.desc}</p>
                <div className="bg-gray-900 rounded-lg p-2 mb-2">
                  <code className="text-xs text-green-400 whitespace-pre-wrap font-mono">{o.env}</code>
                </div>
                <a href={o.url} target="_blank" rel="noopener noreferrer"
                  className="text-xs text-blue-600 hover:underline">{o.url} →</a>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
